import { useTranslation } from "react-i18next";
import { ORDER_STATUS_LABELS, type OrderStatus } from "@ckgo2/shared/types";
import { enumLabel } from "@/i18n/enumLabels";
import type { Participant } from "./types";

// Statuses an admin may start a manual order in (excludes completed/cancelled).
const INITIAL_STATUSES: OrderStatus[] = [
  "inquiry",
  "quoted",
  "confirmed",
  "deposit_paid",
  "fully_paid",
];

interface DetailsStepProps {
  adultsCount: number;
  setAdultsCount: (value: number) => void;
  childrenCount: number;
  setChildrenCount: (value: number) => void;
  participants: Participant[];
  setParticipants: (value: Participant[]) => void;
  status: string;
  setStatus: (value: string) => void;
  customerNotes: string;
  setCustomerNotes: (value: string) => void;
  internalNotes: string;
  setInternalNotes: (value: string) => void;
  canProceedToReview: boolean;
  onBack: () => void;
  onNext: () => void;
}

export default function DetailsStep({
  adultsCount,
  setAdultsCount,
  childrenCount,
  setChildrenCount,
  participants,
  setParticipants,
  status,
  setStatus,
  customerNotes,
  setCustomerNotes,
  internalNotes,
  setInternalNotes,
  canProceedToReview,
  onBack,
  onNext,
}: DetailsStepProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
        {t("orders.new.detailsTitle")}
      </h2>

      {/* Participants count */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t("orders.new.adultsCount")}
          </label>
          <input
            type="number"
            min={1}
            max={10}
            value={adultsCount}
            onChange={(e) => setAdultsCount(parseInt(e.target.value) || 1)}
            className="input"
          />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t("orders.new.childrenCount")}
          </label>
          <input
            type="number"
            min={0}
            max={10}
            value={childrenCount}
            onChange={(e) => setChildrenCount(parseInt(e.target.value) || 0)}
            className="input"
          />
        </div>
      </div>

      {/* Participants */}
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t("orders.new.travelers")}
        </label>
        <div className="space-y-2">
          {participants.map((p, index) => (
            <div key={index} className="flex items-center gap-2">
              <span className="w-8 text-sm text-gray-500">{index + 1}.</span>
              <input
                type="text"
                placeholder={t("orders.new.travelerPlaceholder", { n: index + 1 })}
                value={p.name}
                onChange={(e) => {
                  const updated = [...participants];
                  updated[index] = { ...p, name: e.target.value };
                  setParticipants(updated);
                }}
                className="input flex-1"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Status */}
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t("orders.new.initialStatus")}
        </label>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="input">
          {INITIAL_STATUSES.map((s) => (
            <option key={s} value={s}>
              {enumLabel(ORDER_STATUS_LABELS, s)}
            </option>
          ))}
        </select>
      </div>

      {/* Notes */}
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t("orders.new.customerNotes")}
        </label>
        <textarea
          rows={2}
          value={customerNotes}
          onChange={(e) => setCustomerNotes(e.target.value)}
          placeholder={t("orders.new.customerNotesPlaceholder")}
          className="input"
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t("orders.new.internalNotes")}
        </label>
        <textarea
          rows={2}
          value={internalNotes}
          onChange={(e) => setInternalNotes(e.target.value)}
          placeholder={t("orders.new.internalNotesPlaceholder")}
          className="input"
        />
      </div>

      <div className="flex justify-between pt-4">
        <button onClick={onBack} className="btn-ghost">
          {t("common.actions.back")}
        </button>
        <button onClick={onNext} disabled={!canProceedToReview} className="btn-primary">
          {t("orders.new.continue")}
        </button>
      </div>
    </div>
  );
}
